import React, { useState, useEffect, useCallback } from 'react';
import { parseDateQuery, entityMatchesDateQuery } from '../utils/dateSearchUtils';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Image,
  Modal,
  ScrollView,
  TextInput,
  Pressable,
  Alert,
  LayoutAnimation,
  UIManager,
} from 'react-native';
import { useRoute, useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  where,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { globalStyles } from '../styles/globalStyles';
import { venueListStyles } from '../styles/venueListStyles';
import { homeStyles } from '../styles/HomeStyles';
import { getResponsiveValue } from '../styles/breakpoints';
import { SearchBar } from '../components/SearchBar';
import PostGridDisplay from '../components/PostGridDisplay';
import { useUser } from '../components/LoginPersistence';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUpdatedColorScheme } from '../contexts/FeatureFlagsContext';
import { getPagePaletteIfEnabled } from '../constants/pageColors';
function getAllTextFromItems(items) {
  if (!Array.isArray(items)) return '';
  const parts = [];
  items.forEach((i) => {
    if (!i || !i.value) return;
    if (i.type === 'text' || i.type === 'link' || i.type === 'date') {
      const v = String(i.value).trim();
      if (v) parts.push(v);
    }
  });
  return parts.join(' ');
}

function getFirstImageUrlFromItems(items) {
  if (!Array.isArray(items)) return null;
  for (const i of items) {
    if (i.type !== 'file' || !i.value) continue;
    try {
      const parsed = JSON.parse(i.value);
      if (parsed?.url) {
        const isImage = (parsed.mimeType && parsed.mimeType.startsWith('image/')) || /\.(jpe?g|png|gif|webp|bmp)$/i.test(parsed.name || '');
        if (isImage) return parsed.url;
      }
    } catch (_) {}
  }
  return null;
}

export default function GearSwapList() {
  const route = useRoute();
  const navigation = useNavigation();
  const { user: currentUser } = useUser();
  const { openMessaging } = useCommunityModal();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [typeFilters, setTypeFilters] = useState({ SALE: false, TRADE: false, ISO: false });
  const [optionsOpenId, setOptionsOpenId] = useState(null);
  const [deleteConfirmListing, setDeleteConfirmListing] = useState(null);

  
  const { theme, isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const gearSwapPalette = getPagePaletteIfEnabled('gearSwap', isDarkMode, updatedColorScheme);
  const screenBg = gearSwapPalette?.pageBackground ?? (isDarkMode ? theme.background.primary : '#ffffff');
  const accentColor = gearSwapPalette?.accent ?? (isDarkMode ? theme.text.primary : '#424251');
  const LISTING_TYPE_COLORS = { SALE: '#0a59f0', TRADE: '#03bb8d', ISO: isDarkMode ? '#787a99' : '#4e5067'  };

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const q = query(
        collection(db, 'gearSwapPosts'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setListings(data);
      return data;
    } catch (error) {
      console.error('GearSwapList fetch error:', error);
      setListings([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchListings();
    }, [fetchListings])
  );

  // Sync search from nav params and filter
  useEffect(() => {
    if (route.params?.searchQuery != null) {
      setSearchQuery(route.params.searchQuery);
    }
  }, [route.params?.searchQuery]);

  // Auto-open post flow when navigated here with openPostFlow param
  useEffect(() => {
    if (route.params?.openPostFlow) {
      handleNewListingPress();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.openPostFlow, route.params?.timestamp]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredListings([]);
      return;
    }
    const term = searchQuery.trim();
    const lower = term.toLowerCase();
    const parsedDate = parseDateQuery(term);

    const filtered = listings.filter(item => {
      // Date-aware match first
      if (parsedDate && entityMatchesDateQuery(item, parsedDate)) return true;
      // Standard text fields
      const displayName = (item.displayName || '').toLowerCase();
      const location = (item.location || '').toLowerCase();
      const listingType = (item.listingType || '').toLowerCase();
      const price = (item.price || '').toString().toLowerCase();
      const gridText = getAllTextFromItems(item.items).toLowerCase();
      return displayName.includes(lower) ||
        location.includes(lower) ||
        listingType.includes(lower) ||
        price.includes(lower) ||
        gridText.includes(lower);
    });
    setFilteredListings(filtered);
  }, [searchQuery, listings]);

  const handleSearch = () => {
    // Filter is driven by searchQuery + listings in useEffect
  };

  const baseList = searchQuery.trim() ? filteredListings : listings;
  const hasAnyTypeFilter = typeFilters.SALE || typeFilters.TRADE || typeFilters.ISO;
  const displayList = hasAnyTypeFilter
    ? baseList.filter(item => typeFilters[item.listingType] === true)
    : baseList;

  const toggleTypeFilter = useCallback((type) => {
    setTypeFilters(prev => ({ ...prev, [type]: !prev[type] }));
  }, []);

  const handleNewListingPress = useCallback(async () => {
    if (!currentUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to post a listing.');
      return;
    }
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'gearSwapPosts'),
        where('userId', '==', currentUser.uid)
      );
      const snap = await getDocs(q);
      if (snap.size >= 3) {
        Alert.alert(
          'Listing limit reached',
          'You can have up to 3 gear swap listings at a time. Please delete one before creating a new listing.'
        );
        return;
      }
      navigation.navigate('GearSwapPostCreate');
    } catch (err) {
      console.error('Error checking gear swap listing limit:', err);
      Alert.alert('Error', 'Could not start a new listing. Please try again.');
    }
  }, [currentUser?.uid, navigation]);

  const ACCENT = '#424251';

  const renderListingPost = (item, typeColor) => {
    const postItems = Array.isArray(item.items) ? item.items : [];
    return (
      <>
        <View style={styles.postCardHeader}>
          <View style={styles.postCardHeaderInputs}>
            <Text style={styles.postCardPostInput} numberOfLines={1}>{item.displayName || 'User'}</Text>
            {item.location ? (
              <Text style={styles.postCardLocationInput} numberOfLines={1}>{item.location}</Text>
            ) : null}
          </View>
          <View style={styles.postCardHeaderListingType}>
            <View style={[styles.listingTypeBadgeCard]}>
              <Text style={[styles.listingTypeBadgeCardText, {color: typeColor}]}>{item.listingType || '—'}</Text>
            </View>
            {item.listingType === 'SALE' && item.price ? (
              <Text style={styles.postCardPrice}>${item.price}</Text>
            ) : null}
          </View>
        </View>
        <PostGridDisplay items={postItems} suppressEmptyWidgetHints />
      </>
    );
  };

  const renderListingCard = (item) => {
    const typeColor = LISTING_TYPE_COLORS[item.listingType] || accentColor;
    const postItems = Array.isArray(item.items) ? item.items : [];
    const canDelete = !!currentUser?.uid && item.userId === currentUser.uid;
    const headerColor = item.headerTextColor || undefined;
    return (
      <TouchableOpacity
        key={item.id}
        style={[styles.listingCard, { borderColor: typeColor, borderWidth: 3 }]}
        onPress={() => setSelectedListing(item)}
        activeOpacity={0.8}
      >
        {/* Full-card background layer */}
        {item.cardBackground?.type === 'color' && item.cardBackground.value ? (
          <View style={[styles.listingCardBgLayer, { backgroundColor: item.cardBackground.value }]} />
        ) : null}
        {item.cardBackground?.type === 'image' && item.cardBackground.value ? (
          <Image source={{ uri: item.cardBackground.value }} style={styles.listingCardBgLayer} resizeMode="cover" />
        ) : null}
        {item.cardBackground?.type === 'video' && item.cardBackground.value && Platform.OS === 'web' ? (
          <video src={item.cardBackground.value} autoPlay loop muted playsInline style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12, zIndex: 0 }} />
        ) : null}
        <View style={styles.postCardHeader}>
          <View style={styles.postCardHeaderInputs}>
            <Text style={[styles.postCardPostInput, headerColor ? { color: headerColor } : undefined]} numberOfLines={1}>{item.displayName || 'User'}</Text>
            {item.location ? (
              <Text style={[styles.postCardLocationInput, headerColor ? { color: headerColor } : undefined]} numberOfLines={1}>{item.location}</Text>
            ) : null}
          </View>
          <View style={styles.postCardHeaderListingType}>
            <View style={[styles.listingTypeBadgeCard]}>
              <Text style={[styles.listingTypeBadgeCardText, { color: typeColor }]}>{item.listingType || '—'}</Text>
            </View>
            {item.listingType === 'SALE' && item.price ? (
              <Text style={styles.postCardPrice}>${item.price}</Text>
            ) : null}
          </View>
          <View style={styles.listingCardActions}>
            <TouchableOpacity
              style={styles.listingCardMessageBtn}
              onPress={(e) => {
                if (e?.stopPropagation) e.stopPropagation();
                setSelectedListing(item);
              }}
            >
              <Image
                source={require('../../assets/icons/paper-plane.png')}
                style={styles.listingCardMessageIcon}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.listingCardOptionsBtn}
              onPress={(e) => {
                if (e?.stopPropagation) e.stopPropagation();
                setOptionsOpenId(prev => (prev === item.id ? null : item.id));
              }}
            >
              <Ionicons name="ellipsis-vertical" size={22} color="#555" />
            </TouchableOpacity>
            {optionsOpenId === item.id && (
              <View style={styles.listingCardOptionsMenu}>
                <TouchableOpacity
                  style={styles.listingCardOptionsItem}
                  onPress={(e) => {
                    if (e?.stopPropagation) e.stopPropagation();
                    setOptionsOpenId(null);
                    navigation.navigate('GearSwapPostCreate', { editListingId: item.id, existingListing: item });
                  }}
                >
                  <Text style={styles.listingCardOptionsItemText}>Edit</Text>
                </TouchableOpacity>
                {canDelete && (
                  <TouchableOpacity
                    style={styles.listingCardOptionsItem}
                    onPress={(e) => {
                      if (e?.stopPropagation) e.stopPropagation();
                      setOptionsOpenId(null);
                      handleDeleteListing(item);
                    }}
                  >
                    <Text style={[styles.listingCardOptionsItemText, { color: '#e74c3c' }]}>Delete</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </View>
        <PostGridDisplay items={postItems} cardBackground={null} suppressEmptyWidgetHints />
      </TouchableOpacity>
    );
  };

  const sendInterestMessage = useCallback(async () => {
    if (!selectedListing?.userId || !messageText.trim()) return;
    if (!currentUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to message the poster.');
      return;
    }
    setSendingMessage(true);
    try {
      const db = getFirestore();
      const actualSenderId = currentUser.uid;
      const targetUserId = selectedListing.userId;
      const participants = [actualSenderId, targetUserId].sort();

      const existingQuery = query(
        collection(db, 'conversations'),
        where('participants', '==', participants),
        where('type', '==', 'direct')
      );
      const existingSnapshot = await getDocs(existingQuery);
      let conversationId;

      if (!existingSnapshot.empty) {
        conversationId = existingSnapshot.docs[0].id;
      } else {
        const conversationData = {
          participants,
          type: 'direct',
          name: null,
          lastMessage: null,
          lastActivity: serverTimestamp(),
          createdAt: serverTimestamp(),
          createdBy: actualSenderId,
          unreadCounts: { [actualSenderId]: 0, [targetUserId]: 0 },
          isActive: true,
        };
        const docRef = await addDoc(collection(db, 'conversations'), conversationData);
        conversationId = docRef.id;
      }

      const senderName = currentUser.displayName || currentUser.username || currentUser.firstName || currentUser.email?.split('@')[0] || 'Someone';
      const senderPhoto = currentUser.photoURL || '';
      const listingPreview = {
        displayName: selectedListing.displayName || 'User',
        location: selectedListing.location || '',
        listingType: selectedListing.listingType || '',
        price: selectedListing.listingType === 'SALE' ? (selectedListing.price || '') : null,
        firstText: getFirstTextFromItems(selectedListing.items || []).slice(0, 150),
        firstImageUrl: getFirstImageUrlFromItems(selectedListing.items || []),
        items: selectedListing.items || [],
      };
      const messageData = {
        text: messageText.trim(),
        senderId: actualSenderId,
        senderName: senderName,
        senderPhoto: senderPhoto,
        timestamp: serverTimestamp(),
        type: 'gear_swap_interest',
        readBy: { [actualSenderId]: serverTimestamp() },
        edited: false,
        gearSwapPostId: selectedListing.id || null,
        listingPreview,
      };
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData);
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: {
          text: messageData.text,
          senderId: messageData.senderId,
          timestamp: messageData.timestamp,
          type: messageData.type,
        },
        lastActivity: serverTimestamp(),
        [`unreadCounts.${targetUserId}`]: increment(1),
      });
      setMessageText('');
      setSelectedListing(null);
      openMessaging(targetUserId, conversationId);
    } catch (error) {
      console.error('Send interest error:', error);
      Alert.alert('Send failed', 'Could not send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  }, [selectedListing, messageText, currentUser, openMessaging]);

  const closeInterestModal = useCallback(() => {
    setSelectedListing(null);
    setMessageText('');
  }, []);

  const handleDeleteListing = useCallback((item) => {
    if (!currentUser?.uid || item.userId !== currentUser.uid) return;
    setOptionsOpenId(null);
    setDeleteConfirmListing(item);
  }, [currentUser?.uid]);

  return (
    <View style={[globalStyles.container, { paddingTop: 80, backgroundColor: screenBg }]}>
      <View style={[globalStyles.overlay, { backgroundColor: screenBg }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 60, marginBottom: 8 }}>
          <TouchableOpacity
            onPress={() => navigation.navigate('CommunityList', route.params?.searchQuery ? { searchQuery: route.params.searchQuery } : undefined)}
            activeOpacity={0.8}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : {}}
          >
            <Text style={[homeStyles.homeSubtitle2, { color: accentColor }]}>Directory</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => navigation.navigate('EventsList', route.params?.searchQuery ? { searchQuery: route.params.searchQuery } : undefined)}
            activeOpacity={0.8}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : {}}
          >
            <Text style={[homeStyles.homeSubtitle2, { color: accentColor }]}>Events</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {}}
            activeOpacity={0.8}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : {}}
          >
            <Text style={[homeStyles.homeSubtitle2, { color: accentColor, textDecorationLine: 'underline' }]}>Gear Swap</Text>
          </TouchableOpacity>
        </View>
        <View style={[venueListStyles.searchContainer, { borderBottomColor: 'rgba(66,66,81,0.3)' }]}>
          <SearchBar
            color={accentColor}
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmit={handleSearch}
            placeholder="Search gear, backline, locations..."
          />
        </View>

        <View style={styles.typeFilterRow}>
          {(['SALE', 'TRADE', 'ISO']).map((type) => {
            const color = LISTING_TYPE_COLORS[type] || accentColor;
            const checked = typeFilters[type];
            return (
              <TouchableOpacity
                key={type}
                style={[styles.typeFilterChip, { borderColor: color }, checked && { backgroundColor: color }]}
                onPress={() => toggleTypeFilter(type)}
                activeOpacity={0.8}
              >
                <View style={[styles.typeFilterCheckbox, { borderColor: color }, checked && { backgroundColor: color }]}>
                  {checked ? <Ionicons name="checkmark" size={14} color="#fff" /> : null}
                </View>
                <Text style={[styles.typeFilterLabel, { color: checked ? '#fff' : color }]}>{type}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <View style={styles.listSection}>
          <View style={venueListStyles.venueListHeaderSection}>
            <View style={{ flex: 1 }} />
            <TouchableOpacity
              style={[venueListStyles.postButtonContainer, { flex: 1 }]}
              onPress={handleNewListingPress}
              activeOpacity={0.8}
            >
              <Text style={[venueListStyles.postButton, { color: accentColor }]}>POST</Text>
            </TouchableOpacity>
            <View style={[venueListStyles.venueCountContainer, { flex: 1 }]}>
              <Text style={[venueListStyles.venueCountText, { color: accentColor }]}>
                {displayList.length} {displayList.length === 1 ? 'listing' : 'listings'} found
              </Text>
            </View>
          </View>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={accentColor} />
              <Text style={[styles.loadingText, { color: accentColor }]}>Loading listings...</Text>
            </View>
          ) : displayList.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={[styles.emptyText, { color: accentColor }]}>No listings yet</Text>
              <Text style={[styles.emptySubtext, { color: accentColor }]}>
                {searchQuery
                  ? 'Try a different search term'
                  : 'Gear Swap listings will appear here. Check back soon!'}
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.communityGrid}>
                {displayList.map((item) => renderListingCard(item))}
              </View>
            </>
          )}
        </View>
      </View>

      <Modal
        visible={!!selectedListing}
        transparent
        animationType="fade"
        onRequestClose={closeInterestModal}
      >
        <Pressable style={styles.modalBackdrop} onPress={closeInterestModal}>
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Message {selectedListing?.displayName || 'poster'}</Text>
              <TouchableOpacity onPress={closeInterestModal} style={styles.modalCloseBtn}>
                <Ionicons name="close" size={24} color={accentColor} />
              </TouchableOpacity>
            </View>
            {selectedListing && (
              <>
                <ScrollView
                  style={styles.modalPostScroll}
                  contentContainerStyle={styles.modalPostScrollContent}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator
                >
                  <View style={[styles.modalPostCard, { borderColor: LISTING_TYPE_COLORS[selectedListing.listingType] || accentColor, borderWidth: 3 }]}>
                    {renderListingPost(selectedListing, LISTING_TYPE_COLORS[selectedListing.listingType] || accentColor)}
                  </View>
                </ScrollView>
                <View style={styles.modalMessageRow}>
                  <TextInput
                    style={styles.modalMessageInput}
                    placeholder="Type your message to the poster..."
                    placeholderTextColor="rgba(255, 255, 255, 0.6)"
                    value={messageText}
                    onChangeText={setMessageText}
                    multiline
                    maxLength={2000}
                  />
                  <TouchableOpacity
                    style={[styles.modalSendBtn, sendingMessage && styles.modalSendBtnDisabled]}
                    onPress={sendInterestMessage}
                    disabled={sendingMessage || !messageText.trim()}
                    activeOpacity={0.8}
                  >
                    {sendingMessage ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={styles.modalSendBtnText}>Send</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Delete listing confirmation Modal */}
      <Modal
        visible={!!deleteConfirmListing}
        transparent
        animationType="fade"
        onRequestClose={() => setDeleteConfirmListing(null)}
      >
        <Pressable style={styles.postModalOverlay} onPress={() => setDeleteConfirmListing(null)}>
          <Pressable style={styles.postModalContent} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.postModalTitle}>Delete listing</Text>
            <Text style={styles.postModalSubtitle}>
              Are you sure you want to delete this listing?
            </Text>
            <View style={styles.postModalFooter}>
              <TouchableOpacity
                onPress={() => setDeleteConfirmListing(null)}
                style={styles.postModalContinueBtn}
              >
                <Text style={[styles.postModalContinueText, { color: '#333' }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={async () => {
                  const item = deleteConfirmListing;
                  if (!item) return;
                  const idToRemove = item.id;
                  setDeleteConfirmListing(null);
                  if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
                    UIManager.setLayoutAnimationEnabledExperimental(true);
                  }
                  LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                  setListings((prev) => prev.filter((i) => i.id !== idToRemove));
                  setFilteredListings((prev) => prev.filter((i) => i.id !== idToRemove));
                  try {
                    const db = getFirestore();
                    await deleteDoc(doc(db, 'gearSwapPosts', idToRemove));
                    fetchListings();
                  } catch (err) {
                    console.error('Error deleting gear swap listing:', err);
                    fetchListings();
                    Alert.alert('Delete failed', 'The listing could not be deleted. The list will refresh.');
                  }
                }}
                style={styles.postModalContinueBtn}
              >
                <Text style={[styles.postModalContinueText, { color: '#e74c3c' }]}>Delete</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  listSection: {
    width: '100%',
    alignSelf: 'stretch',
  },
  communityGrid: {
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
    marginTop: 35,
    display: Platform.OS === 'web' ? 'grid' : 'flex',
    ...(Platform.OS === 'web' && {
      gridTemplateColumns: getResponsiveValue({
        mobile: 'repeat(1, 1fr)',
        tablet: 'repeat(2, 1fr)',
        laptop: 'repeat(3, 1fr)',
        desktop: 'repeat(3, 1fr)'
      }),
      gap: getResponsiveValue({
        mobile: 15,
        tablet: 15,
        laptop: 25,
        desktop: 30
      }),
      alignItems: 'center',
      justifyItems: getResponsiveValue({
        mobile: 'center',
        tablet: 'stretch',
        laptop: 'stretch',
        desktop: 'stretch'
      }),
      paddingHorizontal: getResponsiveValue({
        mobile: 0,
        tablet: 20,
        laptop: 20,
        desktop: 20
      }),
      marginBottom: getResponsiveValue({
        mobile: 20,
        tablet: 30,
        laptop: 40,
        desktop: 40
      }),
    }),
    ...(Platform.OS !== 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: 20,
    }),
  },
  listingCard: {
    borderRadius: 12,
    overflow: 'visible',
    padding: 16,
    alignSelf: 'stretch',
    width: '100%',
    alignItems: 'center',
    position: 'relative',
  },
  listingCardBgLayer: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: 0,
  },
  typeFilterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  typeFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 2,
  },
  typeFilterCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeFilterLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '600',
  },
  postCardHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12,
    paddingBottom: 12,
    zIndex: 10,
    width: '100%',
  },
  postCardHeaderInputs: { flex: 1, minWidth: 0 },
  postCardPostInput: {
    fontFamily: 'Computer Says No',
    fontSize: 28,
    color: '#000',
    paddingVertical: 2,
  },
  postCardLocationInput: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#333',
    paddingVertical: 2,
  },
  postCardPrice: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '700',
    color: '#000',
  },
  postCardHeaderListingType: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  listingCardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    position: 'relative',
  },
  listingCardMessageBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    borderRadius: 8,
  },
  listingCardMessageIcon: {
    width: 20,
    height: 20,
  },
  listingCardOptionsBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  listingCardOptionsMenu: {
    position: 'absolute',
    top: 28,
    right: 0,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    minWidth: 120,
    zIndex: 10,
  },
  listingCardOptionsItem: {
    paddingVertical: 6,
  },
  listingCardOptionsItemText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#333',
  },
  listingTypeBadgeCard: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    borderRadius: 8,
  },
  listingTypeBadgeCardText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '600',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 440,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  modalTitle: {
    fontFamily: 'Glacial Indifference',
    fontSize: 18,
    fontWeight: '600',
    color: '#424251',
  },
  modalCloseBtn: { padding: 8 },
  modalPostScroll: {
    ...(Platform.OS === 'web' ? { maxHeight: '70vh' } : { maxHeight: 440 }),
    width: '100%',
  },
  modalPostScrollContent: { padding: 16, paddingBottom: 24, flexGrow: 1 },
  modalPostCard: {
    borderRadius: 12,
    overflow: 'hidden',
    padding: 16,
    alignItems: 'center',
  },
  modalMessageRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  modalMessageInput: {
    flex: 1,
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#000',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
    maxHeight: 100,
  },
  modalSendBtn: {
    backgroundColor: '#424251',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 72,
  },
  modalSendBtnDisabled: { opacity: 0.6 },
  modalSendBtnText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  userCard: {
    backgroundColor: '#feffe4',
    borderRadius: 12,
    height: getResponsiveValue({
      mobile: 380,
      tablet: 440,
      laptop: 480,
      desktop: 500
    }),
    position: 'relative',
    overflow: 'hidden',
    width: getResponsiveValue({
      mobile: '90vw',
      tablet: '100%',
      laptop: '100%',
      desktop: '100%'
    }),
    marginLeft: getResponsiveValue({
      mobile: 'auto',
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
    marginRight: getResponsiveValue({
      mobile: 'auto',
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
    marginBottom: Platform.OS !== 'web' ? 16 : 0,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      ':hover': {
        transform: 'scale(1.02)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.3)',
      },
    }),
  },
  profileCard: {
    height: '100%',
    width: '100%',
    paddingTop: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    paddingBottom: getResponsiveValue({
      mobile: 35,
      tablet: 40,
      laptop: 45,
      desktop: 50
    }),
    gap: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 6,
      desktop: 8
    }),
    alignItems: 'center',
    position: 'relative',
  },
  profileImagePlaceholder: {
    width: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    height: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    borderRadius: getResponsiveValue({
      mobile: 30,
      tablet: 35,
      laptop: 40,
      desktop: 45
    }),
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
  },
  profileImagePlaceholderText: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 24,
      tablet: 28,
      laptop: 32,
      desktop: 36
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  profileName: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 16,
      tablet: 18,
      laptop: 20,
      desktop: 22
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 8,
      desktop: 8
    }),
  },
  listingTypeBadge: {
    alignSelf: 'center',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginBottom: 6,
  },
  listingTypeBadgeText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  priceText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '700',
    color: '#000',
    marginBottom: 4,
  },
  locationText: {
    color: 'rgba(0, 0, 0, 0.7)',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 14,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 6,
      tablet: 8,
      laptop: 10,
      desktop: 10
    }),
  },
  bioText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: getResponsiveValue({
      mobile: 11,
      tablet: 12,
      laptop: 13,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    lineHeight: getResponsiveValue({
      mobile: 16,
      tablet: 18,
      laptop: 20,
      desktop: 22
    }),
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 200,
  },
  loadingText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    marginTop: 15,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 200,
  },
  emptyText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 10,
    textAlign: 'center',
  },
  emptySubtext: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  postModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  postModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 25,
    paddingVertical: 12,
    paddingHorizontal: 28,
    width: '100%',
    maxWidth: 700,
    maxHeight: '90%',
  },
  postModalTitle: {
    fontSize: 32,
    fontFamily: 'Playpen Sans',
    color: '#fbcd9d',
    textAlign: 'center',
    marginBottom: 18,
  },
  postModalSubtitle: {
    fontFamily: 'Glacial Indifference',
    fontSize: 16,
    color: '#166fff',
    textAlign: 'center',
    marginBottom: 24,
  },
  postModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 8,
  },
  postModalContinueBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  postModalContinueText: {
    fontFamily: 'Glacial Indifference',
    fontSize: 16,
    fontWeight: '600',
    color: '#166fff',
  },
});
